import * as Yup from 'yup';
import { Formik } from 'formik';
import React, { Fragment } from 'react';
import { TimezoneDropdown } from 'components/timezones/shared/TimezoneDropdown';
import { LocationDropdown } from 'components/layout/form-fields/inputs/LocationDropdown';
import { ModalComponent } from 'components/layout/modals/ModalComponent';
import { FormField as FormikFormField } from 'components/layout/form-fields/FormField';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import {
	FormRow,
	FormSection,
	FormField
} from 'components/layout/modals/components';
import { ClientsDropdown } from 'components/layout/form-fields/inputs/ClientsDropdown';
import { ClientContactsDropdown } from 'components/layout/form-fields/inputs/ClientContactsDropdown';
import UserAssignmentInput from 'ats/components/user-assignment-input/UserAssignmentInput';
import MultiDropdownEditable from 'ats/components/multidropdown-editable/MultiDropdownEditable';
import { authStore } from 'shared/security/AuthStore';
import { NON_CANDIDATE_USER_ROLES } from 'shared/security/RolesAllowed';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { JobRoleDropdown } from 'components/layout/form-fields/inputs/JobRoleDropdown';
import { openJobPositionProfile } from 'ats/utils/openEntityProfile';

const ValidationSchema = Yup.object().shape({
	title: Yup.string().required().label('Title'),
	timezones: Yup.array().nullable().label('Timezones'),
	locations: Yup.array().nullable().label('Locations'),
	clientId: Yup.string().nullable().required().label('Client'),
	owners: Yup.array().nullable().label('Owner'),
	hiringManagerId: Yup.string().nullable().label('Hiring Manager'),
	accountManagerId: Yup.string().nullable().required().label('Account Manager'),
	talentLeaderId: Yup.number().nullable().label('Talent Leader'),
	roleId: Yup.number().nullable().label('Role'),
	priority: Yup.string().required().label('Priority'),
	urgency: Yup.string().label('Urgency'),
	vacancies: Yup.number().nullable().min(1).max(20).label('Vacancies')
});

export class JobPositionCreate extends ModalComponent {
	static isV2 = true;

	getInitialValues() {
		const { clientId = null, client = null } = this.props;

		return {
			title: '',
			priority: jobPositionStore.priorityOptions[0].value,
			urgency: 'follow_up',
			status: 'draft',
			disabled: true,
			vacancies: 1,
			clientId: clientId,
			client: client,
			timezones: [],
			locations: [],
			owners: [],
			hiringManagerId: null,
			hiringManager: null,
			accountManagerId: null,
			accountManager: null,
			talentLeaderId: null,
			talentLeader: null,
			roleId: null,
			role: null
		};
	}

	getMiddleButton = formikBag => {
		return {
			label: 'Create & Add Job Description',
			disabled: !formikBag.dirty,
			loading: this.state.saving,
			onClick: e => {
				e.preventDefault();
				this.saving();
				this.createAndOpenDescription(formikBag.values);
			}
		};
	};

	render() {
		return (
			<Formik
				initialValues={this.getInitialValues()}
				validationSchema={ValidationSchema}
				validateOnChange={false}
				validateOnBlur={false}
				onSubmit={values => {
					this.saving();
					this.create(values);
				}}>
				{formikBag => this.renderModalContainer(formikBag, 'JobPositionCreate')}
			</Formik>
		);
	}

	renderForm(formikBag) {
		return (
			<Fragment>
				<FormRow>
					<FormSection>
						<FormField
							label="Title"
							required
							variant="dashed"
							error={formikBag.errors.title}>
							<FormikFormField
								className="has-text-left"
								field="title"
								type="text"
								placeholder=" "
								maxlength={255}
								showErrors={false}
							/>
						</FormField>
						<FormField
							label="Client"
							required
							variant="dashed"
							error={formikBag.errors.clientId}>
							<ClientsDropdown
								value={formikBag.values.client}
								placeholder=" "
								isClearable
								styleVersion="ats"
								onValueChange={value => {
									formikBag.setFieldValue('client', value);
									formikBag.setFieldValue('clientId', value?.id);

									if (!value) {
										formikBag.setFieldValue('hiringManager', null);
										formikBag.setFieldValue('hiringManagerId', null);
									}

									if (
										value?.accountManager &&
										!formikBag.values.accountManager
									) {
										formikBag.setFieldValue(
											'accountManager',
											value.accountManager
										);
										formikBag.setFieldValue(
											'accountManagerId',
											value.accountManager.id
										);
									}
								}}
							/>
						</FormField>
						<FormField
							label="Hiring Manager"
							variant="dashed"
							error={formikBag.errors.hiringManagerId}>
							<ClientContactsDropdown
								value={formikBag.values.hiringManager}
								placeholder=" "
								isClearable
								styleVersion="ats"
								clientId={formikBag.values.clientId}
								disabled={!formikBag.values.client}
								onValueChange={value => {
									formikBag.setFieldValue('hiringManager', value);
									formikBag.setFieldValue('hiringManagerId', value?.id ?? null);
								}}
							/>
						</FormField>
						<FormField
							label="Urgency"
							variant="dashed"
							error={formikBag.errors.urgency}>
							<Dropdown
								value={formikBag.values.urgency}
								placeholder=" "
								onChange={item => {
									formikBag.setFieldValue('urgency', item);
								}}
								options={jobPositionStore.urgencyOptions}
								optionValue="value"
							/>
						</FormField>
						<FormField
							label="Priority"
							required
							variant="dashed"
							error={formikBag.errors.priority}>
							<Dropdown
								value={formikBag.values.priority}
								placeholder=" "
								onChange={item => {
									formikBag.setFieldValue('priority', item);
								}}
								options={jobPositionStore.priorityOptions}
								optionValue="value"
							/>
						</FormField>
						<FormField
							label="Vacancies"
							required
							variant="dashed"
							error={formikBag.errors.vacancies}>
							<FormikFormField
								field="vacancies"
								type="number"
								max={20}
								min={1}
								placeholder=" "
								showErrors={false}
							/>
						</FormField>
						<FormField
							label="Role"
							variant="dashed"
							error={formikBag.errors.roleId}>
							<JobRoleDropdown
								isMulti={false}
								value={formikBag.values.role}
								placeholder=" "
								styleVersion="ats"
								onValueChange={value => {
									formikBag.setFieldValue('role', value);
									formikBag.setFieldValue('roleId', value.id);
								}}
							/>
						</FormField>
					</FormSection>
					<FormSection title="Details" size={2}>
						<FormField
							label="Account Manager"
							required
							error={formikBag.errors.accountManagerId}>
							<UserAssignmentInput
								value={formikBag.values.accountManager}
								placeholder=" "
								role={['Account Manager']}
								minSearchChars={0}
								onChange={value => {
									formikBag.setFieldValue('accountManager', value);
									formikBag.setFieldValue('accountManagerId', value.id);
								}}
							/>
						</FormField>
						<FormField
							label="Talent Leader"
							error={formikBag.errors.talentLeaderId}>
							<UserAssignmentInput
								value={formikBag.values.talentLeader}
								placeholder=" "
								role={['Talent Leader']}
								minSearchChars={0}
								onChange={value => {
									formikBag.setFieldValue('talentLeader', value);
									formikBag.setFieldValue('talentLeaderId', value.id);
								}}
							/>
						</FormField>
						<FormField label="Owner" error={formikBag.errors.owners}>
							<UserAssignmentInput
								value={formikBag.values.owners}
								isMulti
								placeholder=" "
								role={NON_CANDIDATE_USER_ROLES}
								onChange={values => {
									formikBag.setFieldValue('owners', values);
								}}
								onAssignToMe={() => {
									const currentUser = authStore.profile;
									const currentOwners = formikBag.values.owners || [];

									// Check if current user is already in the owners list
									const isAlreadyOwner = currentOwners.some(
										owner => owner.id === currentUser.id
									);

									if (!isAlreadyOwner) {
										formikBag.setFieldValue('owners', [
											...currentOwners,
											{
												...currentUser,
												isPrimary: currentOwners.length === 0
											}
										]);
									}
								}}
							/>
						</FormField>
						<FormField label="Locations" error={formikBag.errors.locations}>
							<MultiDropdownEditable
								values={formikBag.values.locations}
								textKey="name"
								onRemoveValue={value => {
									formikBag.setFieldValue(
										'locations',
										formikBag.values.locations.filter(v => v.id !== value.id)
									);
								}}>
								<LocationDropdown
									isMulti
									value={formikBag.values.locations}
									placeholder=" "
									styleVersion="ats"
									onValueChange={values => {
										formikBag.setFieldValue('locations', values);
									}}
								/>
							</MultiDropdownEditable>
						</FormField>
						<FormField label="Timezones" error={formikBag.errors.timezones}>
							<MultiDropdownEditable
								values={formikBag.values.timezones}
								textKey="text"
								onRemoveValue={value => {
									formikBag.setFieldValue(
										'timezones',
										formikBag.values.timezones.filter(v => v.id !== value.id)
									);
								}}>
								<TimezoneDropdown
									isMulti
									value={formikBag.values.timezones}
									placeholder=" "
									styleVersion="ats"
									onValueChange={values => {
										formikBag.setFieldValue('timezones', values);
									}}
								/>
							</MultiDropdownEditable>
						</FormField>
					</FormSection>
				</FormRow>
			</Fragment>
		);
	}

	openCreatedProfile(jobPositionId, extraProps = {}) {
		openJobPositionProfile(jobPositionId, {
			onSuccess: () => this.props.handleSuccess?.(),
			...extraProps
		});
	}

	async create(values) {
		jobPositionStore
			.create({
				...values
			})
			.then(result => {
				this.resultNotification(result, 'Job Position', 'Create');
				if (result.status === 'success') {
					this.openCreatedProfile(result.data?.id);
				}
			});
	}

	async createAndOpenDescription(values) {
		try {
			const result = await jobPositionStore.create({
				...values
			});

			if (result.status === 'success') {
				this.closeWithSuccess(result);
				this.openCreatedProfile(result.data?.id, {
					initialTab: 1, // TABS.DESCRIPTION
					initialEditingMode: true
				});
			} else {
				this.resultNotification(result, 'Job Position', 'Create');
			}
		} catch (error) {
			console.error('Error creating job position:', error);
			this.saving(false);
		}
	}
}
